import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { api } from "../utils/api";

const DAYS = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const DURATIONS = [15, 30, 45, 60, 90, 120];
const PRICES = [5, 10, 15, 20, 25, 30, 40, 50, 60, 75, 100, 150, 200];

type Slot = { id: string; day_of_week: number; start_time: string; end_time: string; slot_duration_minutes?: number; price?: number | string | null };
type Resource = { type: "power" | "venue"; id: string; label: string; category?: string };

// Gestion des créneaux récurrents hebdomadaires d'un power ou d'un lieu (ex: Bricolage tous
// les samedis 9h-12h) — c'est ce motif qui alimente ensuite les créneaux concrets à réserver.
export default function AvailabilityModal({ resource, onClose }: { resource: Resource; onClose: () => void }) {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(1);
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("12:00");
  const [duration, setDuration] = useState(60);
  const [price, setPrice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isVenue = resource.type === "venue";
  const catQs = isVenue ? `?category=${encodeURIComponent(resource.category || "")}` : "";
  const listPath = isVenue ? `/venues/${resource.id}/availability${catQs}` : `/bookings/availability/${resource.id}`;
  const createPath = isVenue ? `/venues/${resource.id}/availability` : `/bookings/availability`;
  const deletePath = (slotId: string) => isVenue ? `/venues/availability/${slotId}` : `/bookings/availability/${slotId}`;

  const load = () => {
    api.get(listPath).then(data => setSlots(data?.availability || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [resource.id, resource.category]);

  const addSlot = () => {
    if (start >= end) { alert("L'heure de fin doit être après l'heure de début"); return; }
    setSubmitting(true);
    const common = { day_of_week: day, start_time: start, end_time: end, slot_duration_minutes: duration, price: price ? Number(price) : null };
    const body = isVenue ? { ...common, category: resource.category || null } : { power_id: resource.id, ...common };
    api.post(createPath, body)
      .then(data => { if (data?.availability) setSlots(prev => [...prev, data.availability]); else alert(data?.error || "Erreur"); })
      .catch(() => alert("Erreur de connexion"))
      .finally(() => setSubmitting(false));
  };

  const removeSlot = (id: string) => {
    setSlots(prev => prev.filter(s => s.id !== id));
    api.delete(deletePath(id)).catch(() => {});
  };

  const grouped = DAYS.map((label, dow) => ({ label, dow, items: slots.filter(s => s.day_of_week === dow).sort((a, b) => a.start_time.localeCompare(b.start_time)) }));

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 420, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 17, fontWeight: 700 }}>{isVenue ? (resource.category ? `Agenda, ${resource.category}` : "Agenda du lieu (général)") : "Mon agenda"}</span>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
        </div>
        <div style={{ fontSize: 13, color: "#999", marginBottom: 18 }}>{resource.label}</div>

        {loading ? (
          <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>Chargement...</div>
        ) : (
          <div style={{ marginBottom: 18 }}>
            {grouped.filter(g => g.items.length > 0).length === 0 && (
              <div style={{ fontSize: 13, color: "#999", padding: "8px 0 16px" }}>Aucun créneau ouvert pour l'instant.</div>
            )}
            {grouped.map(g => g.items.length > 0 && (
              <div key={g.dow} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#666", marginBottom: 4 }}>{g.label}</div>
                {g.items.map(s => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", backgroundColor: "#F8F8F8", borderRadius: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 13 }}>
                      {s.start_time.slice(0, 5)} – {s.end_time.slice(0, 5)}
                      <span style={{ color: "#999" }}> · rdv de {s.slot_duration_minutes || 60} min{s.price != null && s.price !== "" ? ` · ${Number(s.price).toFixed(2)} €` : ""}</span>
                    </span>
                    <button onClick={() => removeSlot(s.id)} style={{ border: "none", background: "none", cursor: "pointer", color: "#c0392b", fontSize: 12, fontWeight: 600 }}>Retirer</button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 8 }}>Ajouter un créneau récurrent</div>
        <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10 }}>
          {DAYS.map((label, dow) => (
            <button key={dow} onClick={() => setDay(dow)} style={{ flexShrink: 0, padding: "7px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer", border: day === dow ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: day === dow ? "#0a0a0a" : "white", color: day === dow ? "white" : "#666" }}>
              {label.slice(0, 3)}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <input type="time" value={start} onChange={e => setStart(e.target.value)} style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, boxSizing: "border-box" }} />
          <input type="time" value={end} onChange={e => setEnd(e.target.value)} style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, boxSizing: "border-box" }} />
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 }}>Durée d'un rendez-vous</div>
        <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10 }}>
          {DURATIONS.map(d => (
            <button key={d} onClick={() => setDuration(d)} style={{ flexShrink: 0, padding: "7px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer", border: duration === d ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: duration === d ? "#0a0a0a" : "white", color: duration === d ? "white" : "#666" }}>
              {d} min
            </button>
          ))}
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 }}>Prix par rendez-vous</div>
        <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 16, paddingBottom: 2 }}>
          <button onClick={() => setPrice("")} style={{ flexShrink: 0, padding: "7px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer", border: price === "" ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: price === "" ? "#0a0a0a" : "white", color: price === "" ? "white" : "#666" }}>
            Gratuit
          </button>
          {PRICES.map(p => (
            <button key={p} onClick={() => setPrice(String(p))} style={{ flexShrink: 0, padding: "7px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer", border: price === String(p) ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: price === String(p) ? "#0a0a0a" : "white", color: price === String(p) ? "white" : "#666" }}>
              {p} €
            </button>
          ))}
        </div>

        <button onClick={addSlot} disabled={submitting} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: submitting ? 0.6 : 1 }}>
          {submitting ? "Ajout..." : "Ajouter ce créneau"}
        </button>
      </div>
    </div>
  ,
    document.body
  );
}
